import React, { useEffect, useRef, useState } from 'react';
import { v4 } from 'uuid';
import './style.less';
import { Icon } from 'shared/components/Icon';
import { useDispatch, useSelector } from 'react-redux';
import { useAppSelector } from 'shared/store';
import { AppState } from 'shared/store/reducers';
import {
  CallTask,
  CallTaskStatus,
  ConnectedCallTaskStates,
  DialTask,
  InProgressCallTaskStates,
  SourceType,
} from 'types/outbound';
import clsx from 'clsx';
import { setMuted } from 'shared/store/outbound-slice';
import { Button } from 'shared/components/Button';
import { createLbbContact, searchLBBContacts } from 'shared/api';
import { formatDuration } from 'shared/utils/helpers';
import { useOutboundContext } from '../../outbound-provider';

const CallDurationAndStatus = () => {
  const [duration, setDuration] = useState<string | null>(null);
  const durationInterval = useRef<NodeJS.Timeout | null>(null);
  const { session } = useOutboundContext();

  const { detailTask: dialTask } = useAppSelector(state => state.outbound);

  const dialTaskConnected = dialTask && ConnectedCallTaskStates.includes(dialTask.status);

  useEffect(() => {
    if (!session.current.callStartedAt) {
      if (!durationInterval.current) return;
      clearInterval(durationInterval.current);

      return;
    }
    durationInterval.current = setInterval(() => {
      const now = new Date();
      const differenceInSeconds = Math.floor(
        /* @ts-ignore */
        (now - session.current.callStartedAt) / 1000,
      );

      setDuration(formatDuration(differenceInSeconds * 1000));
    }, 1000);

    return () => {
      if (!durationInterval.current) return;
      clearInterval(durationInterval.current);
    };
  }, [session, session.current.callStartedAt]);

  if (!dialTaskConnected) return <></>;

  return (
    <>
      <div
        className={clsx(
          'ConnectedCallDetailHeader__status',
          !dialTaskConnected && 'ConnectedCallDetailHeader__status--disconnected',
        )}
      >
        {dialTaskConnected ? 'Connected' : 'Call ended'}
      </div>
      {!!duration && (
        <div className="ConnectedCallDetailHeader__duration">
          {!dialTaskConnected && (
            <>
              <span className="ConnectedCallDetailHeader__duration-label">Duration:</span>
            </>
          )}
          {duration}
        </div>
      )}
    </>
  );
};

const Mute = () => {
  const { detailTask: dialTask } = useAppSelector(state => state.outbound);

  const dialTaskConnected = dialTask && ConnectedCallTaskStates.includes(dialTask.status);

  const dispatch = useDispatch();
  const { muted } = useSelector((state: AppState) => state.outbound);

  if (!dialTaskConnected) return <></>;

  return (
    <button
      className="ConnectedCallDetailHeader__actions-item"
      onClick={() => dispatch(setMuted(!muted))}
    >
      <Icon width={20} height={20} icon={!muted ? 'micOnOutline' : 'micOffOutline'} />
      {!muted ? 'Mute' : 'Unmute'}
    </button>
  );
};

export const ConnectedCallDetailHeader: React.FC<{
  isConnected: boolean;
  isDialing: boolean;
  isOpen: boolean;
  close: () => void;
  onCallNextClick: (task: CallTask) => Promise<void>;
  onDial: (task: CallTask) => Promise<void>;
  onDialWithoutTask: (task: CallTask) => Promise<void>;
  onInvalidNumberClick: (task: CallTask, number: string) => Promise<void>;
  onSkip: () => Promise<void>;
  onStop: () => Promise<void>;
}> = ({
  isConnected,
  isDialing,
  isOpen,
  close,
  onCallNextClick,
  onDial,
  onDialWithoutTask,
  onInvalidNumberClick,
  onSkip,
  onStop,
}) => {
  const { detailTask: dialTask, sourceType } = useSelector(
    (state: AppState) => state.outbound,
  );

  const dialTaskInProgress =
    dialTask &&
    [...ConnectedCallTaskStates, ...InProgressCallTaskStates].includes(dialTask.status);

  const attemptedNumbers = dialTask?.attemptedNumbers ?? [];
  const invalidNumbers = dialTask?.invalidNumbers ?? [];
  const dialTaskNumber =
    dialTask && dialTask.contact && 'number' in dialTask && dialTask.number;
  const dialTaskNumberValid = dialTaskNumber && !invalidNumbers.includes(dialTaskNumber);

  const canCallNextNumber = (() => {
    if (!dialTaskNumber || !dialTask.contact.phoneNumbers) {
      return false;
    }
    for (const data of dialTask.contact.phoneNumbers) {
      if (
        !attemptedNumbers.includes(data.number) &&
        !invalidNumbers.includes(data.number) &&
        dialTaskNumber !== data.number
      ) {
        return true;
      }
    }

    return false;
  })();

  return (
    <div className="ConnectedCallDetailHeader">
      <h2 className="ConnectedCallDetailHeader__title">
        <Icon
          width={24}
          height={24}
          icon={dialTaskInProgress ? 'phoneCall' : 'phoneOffOutline'}
        />
        Prospect
        <CallDurationAndStatus />
      </h2>
      <div className="ConnectedCallDetailHeader__actions">
        <Mute />
        {dialTaskInProgress && (
          <Button
            className="ConnectedCallDetailHeader__button ConnectedCallDetailHeader__button--end"
            size="small"
            type="primary"
            onClick={async () => await onStop()}
          >
            <Icon width={16} height={16} icon="phoneOff" />
            End call
          </Button>
        )}
        {!isDialing && !isConnected && (
          <Button
            className="ConnectedCallDetailHeader__button"
            size="small"
            type="primary"
            onClick={async () => {
              if (dialTask?.status === CallTaskStatus.PENDING) {
                await onDial(dialTask as CallTask);
                return;
              }

              const contact = await (async () => {
                if (dialTask?.contact) {
                  return dialTask.contact;
                }
                const { data } = await searchLBBContacts(
                  dialTask?.contact?.phone ?? (dialTask as DialTask)?.number ?? '',
                );
                if (data.length) {
                  return data[0];
                }
                return await createLbbContact({
                  phone: dialTask?.contact?.phone,
                });
              })();

              await onDialWithoutTask({
                frontendId: dialTask?.frontendId ?? v4(),
                sourceId: -1,
                sourceType: SourceType.DIALPAD,
                status: CallTaskStatus.PENDING,
                contact: {
                  name: contact.name,
                  firstName: contact.firstName,
                  lastName: contact.lastName,
                  id: contact.id,
                  company: contact.company,
                  email: contact.email,
                  phone: contact.phone,
                  phoneNumbers: contact.phoneNumbers,
                  customMetadata: contact.customMetadata,
                  associatedServices: contact.associatedServices,
                },
              });
            }}
            disabled={!dialTask?.contact?.phone}
          >
            <Icon width={16} height={16} icon="phoneOn" />
            Call
          </Button>
        )}

        {canCallNextNumber && isOpen && (
          <Button
            className="ConnectedCallDetailHeader__button"
            type="ghost"
            size="small"
            onClick={async () => await onCallNextClick(dialTask as CallTask)}
            disabled={!isOpen}
          >
            <Icon width={16} height={16} icon="call" />
            Call next number for contact
          </Button>
        )}

        {isOpen && dialTaskNumberValid && (
          <Button
            className="ConnectedCallDetailHeader__button ConnectedCallDetailHeader__button--ghost"
            type="ghost"
            size="small"
            onClick={async () => {
              if (dialTaskNumber) {
                await onInvalidNumberClick(dialTask as CallTask, dialTaskNumber);
              }
            }}
            disabled={!isOpen}
          >
            <Icon width={16} height={16} icon="block" />
            Number invalid
          </Button>
        )}

        {sourceType !== SourceType.DIALPAD && dialTaskInProgress && (
          <Button
            className="ConnectedCallDetailHeader__button ConnectedCallDetailHeader__button--ghost"
            type="ghost"
            size="small"
            onClick={async () => await onSkip()}
            disabled={!isOpen}
          >
            Skip to next call
          </Button>
        )}

        <button className="ConnectedCallDetailHeader__close" onClick={close}>
          <Icon icon="close" width={20} height={20} />
        </button>
      </div>
    </div>
  );
};
